import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OfficeRunningSheets from "@/components/OfficeRunningSheets";
import LoadDialog from "@/components/RunningSheetLoadDialog";
import { AuthImage } from "@/components/AuthImage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Truck, Plus, Send, Trash2, ChevronDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function RunningSheets() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const selectedSheet = params.get("sheet");
  const isOffice = ["admin", "office", "super_admin"].includes(user.role);
  const [exporting, setExporting] = useState(null);
  const exportPdf = async sheet => {
    setExporting(sheet.id);
    try {
      const {data} = await api.get(`/truck-sheets/${sheet.id}/pdf`, {responseType:"blob", __flSkipReadCache:true});
      const url = URL.createObjectURL(data); const link = document.createElement("a");
      link.href = url; link.download = `running-sheet-${sheet.date}.pdf`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {toast.error("Could not export the run sheet. Please retry.");}
    finally {setExporting(null);}
  };
  const [sheets, setSheets] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loadDialog, setLoadDialog] = useState(null); // { sheetId, load? }
  const [startTruck, setStartTruck] = useState("");
  const [startTrailer, setStartTrailer] = useState("");
  const [busy, setBusy] = useState(false);
  const [docketPhoto, setDocketPhoto] = useState(null);

  useEffect(() => {
    if (sheets && selectedSheet) document.getElementById(`sheet-${selectedSheet}`)?.scrollIntoView({ block: "start" });
  }, [sheets, selectedSheet]);

  const load = () => {
    api.get("/truck-sheets", { params: selectedSheet ? { sheet_id: selectedSheet } : {} }).then((r) => setSheets(r.data.items || [])).catch((e) => {
      toast.error(e.response?.data?.detail || "Failed to load running sheets");
      setSheets([]);
    });
  };
  useEffect(() => {
    load();
    api.get("/fleet").then((r) => setFleet((r.data || []).filter((v) => v.status === "active"))).catch(() => {});
    api.get("/jobs").then((r) => setJobs((r.data || []).filter((j) => j.status !== "completed"))).catch(() => {});
  }, [selectedSheet]);

  const todaySheet = useMemo(
    () => (sheets || []).find((s) => s.date === todayIso() && s.user_id === user.id),
    [sheets, user.id],
  );

  const startDay = async () => {
    if (!startTruck) { toast.error("Pick your truck first"); return; }
    setBusy(true);
    try {
      await api.post("/truck-sheets", { date: todayIso(), truck_id: startTruck, trailer_id: startTrailer || null });
      toast.success("Running sheet started — add your first load");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the sheet");
    } finally { setBusy(false); }
  };

  const submitSheet = async (s) => {
    if (!window.confirm(`Submit ${s.date} running sheet with ${s.loads.length} load(s)? You won't be able to change it after.`)) return;
    try {
      await api.post(`/truck-sheets/${s.id}/submit`);
      toast.success("Running sheet submitted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  const deleteLoad = async (sheetId, loadId) => {
    if (!window.confirm("Remove this load?")) return;
    try { await api.delete(`/truck-sheets/${sheetId}/loads/${loadId}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteSheet = async (s) => {
    if (!window.confirm(`Delete the whole ${s.date} sheet?`)) return;
    try { await api.delete(`/truck-sheets/${s.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className={`p-6 lg:p-10 ${isOffice ? "max-w-7xl" : "max-w-5xl"} mx-auto pb-24`} data-testid="running-sheets-page">
      {!isOffice && <Link to="/app/my-runs" className="inline-flex min-h-12 items-center text-sm text-orange-700 dark:text-orange-300">← My trucking runs</Link>}
      <PageHeader
        tag="/ Carting"
        title={isOffice ? "Truck running sheets" : "Run history"}
        subtitle={isOffice
          ? "Every driver's daily carting record — loads, materials, dockets and quantities for billing."
          : "Your saved loads by day. Review them and submit your day when finished."}
      />

      {selectedSheet && <Link className="block text-xs text-[#FF5F15] mb-3" to={isOffice ? "/app/trucking/running-sheets" : "/app/running-sheets"}>{isOffice ? "All Running Sheets →" : "All history →"}</Link>}
      {/* Driver: start today's sheet — prominent, no dispatch needed */}
      {!isOffice && sheets && !todaySheet && (
        <div className="hard-card p-4 mb-4 border-2 border-[#FF5F15]/40" data-testid="start-sheet-card">
          <div className="text-sm font-bold mb-1 flex items-center gap-1.5"><Truck className="w-4 h-4 text-[#FF5F15]" /> Start today’s running sheet</div>
          <p className="text-xs text-zinc-500 mb-3">Pick your truck and start your day — no dispatch or office assignment needed. You can add loads straight away.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="label-tag">Truck *</Label>
              <select value={startTruck} onChange={(e) => setStartTruck(e.target.value)}
                      className="mt-2 w-full h-12 px-3 text-sm bg-background border border-border rounded-sm focus:outline-none focus:border-[#FF5F15]"
                      data-testid="start-truck-select">
                <option value="">Pick your truck…</option>
                {fleet.map((v) => <option key={v.id} value={v.id}>{v.rego} — {v.make} {v.model}</option>)}
              </select>
            </div>
            <div>
              <Label className="label-tag">Trailer (optional)</Label>
              <select value={startTrailer} onChange={(e) => setStartTrailer(e.target.value)}
                      className="mt-2 w-full h-12 px-3 text-sm bg-background border border-border rounded-sm focus:outline-none focus:border-[#FF5F15]"
                      data-testid="start-trailer-select">
                <option value="">No trailer</option>
                {fleet.map((v) => <option key={v.id} value={v.id}>{v.rego} — {v.make} {v.model}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={startDay} disabled={busy}
                      className="w-full h-12 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
                      data-testid="start-sheet-btn">
                {busy ? "Starting…" : "Start today’s sheet"}
              </Button>
            </div>
          </div>
          {fleet.length === 0 && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">No active trucks found in your fleet — add a truck under Plant &amp; Fleet, or ask the office to add one.</p>
          )}
        </div>
      )}

      {!sheets ? (
        <div className="text-xs text-zinc-500 font-mono">Loading…</div>
      ) : sheets.length === 0 ? (
        <EmptyState title={isOffice ? "No running sheets yet" : "No run history yet"}
                    description={isOffice ? "Sheets will appear here as drivers start their day." : "Your loads will appear here when you save them from My trucking runs."} />
      ) : isOffice ? (
        <OfficeRunningSheets sheets={sheets} selectedSheet={selectedSheet} userId={user.id}
          exporting={exporting} onExport={exportPdf} onDocket={setDocketPhoto}
          onDeleteLoad={deleteLoad} onDeleteSheet={deleteSheet} onSubmit={submitSheet}
          onAdd={sheetId => setLoadDialog({ sheetId })} />
      ) : (
        <div className="space-y-4">
          {sheets.map((s) => {
            const open = expanded[s.id] ?? (s.id === selectedSheet || (isOffice && s.id === todaySheet?.id));
            const isMineDraft = s.user_id === user.id && s.status === "draft";
            const canModify = isMineDraft || isOffice;
            return (
              <div key={s.id} id={`sheet-${s.id}`} className="hard-card overflow-hidden" data-testid={`sheet-${s.id}`}>
                <button type="button" onClick={() => setExpanded((x) => ({ ...x, [s.id]: !open }))}
                        aria-expanded={open} className="w-full px-4 py-4 min-h-12 flex items-center gap-3 text-left hover:bg-muted/40">
                  {isOffice && <span className="w-10 h-10 bg-[#09090B] text-[#FF5F15] flex items-center justify-center shrink-0">
                    <Truck className="w-5 h-5" />
                  </span>}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">
                      {s.date} · <span className="font-mono">{s.truck_rego}</span>
                      {s.trailer_rego && <span className="font-mono text-zinc-500"> + {s.trailer_rego}</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                      {isOffice ? `${s.user_name} · ` : ""}{s.totals.loads} load{s.totals.loads === 1 ? "" : "s"}
                      {s.totals.tonnes > 0 && ` · ${s.totals.tonnes}t`}
                      {s.totals.cubic_m > 0 && ` · ${s.totals.cubic_m}m³`}
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-[9px] font-bold uppercase tracking-widest border ${s.status === "submitted" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-amber-400 bg-amber-50 text-amber-700"}`}
                        data-testid={`sheet-status-${s.id}`}>
                    {isOffice ? s.status : s.status === "draft" ? "Not submitted" : s.status === "submitted" ? "Submitted" : s.status}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>

                {isOffice && <div className="px-4 pb-3"><Button variant="outline" disabled={!!exporting} onClick={() => exportPdf(s)}>{exporting === s.id ? "Exporting…" : "Export PDF"}</Button></div>}
                {open && (
                  <div className="border-t border-zinc-200">
                    {(s.loads || []).length === 0 ? (
                      <div className="px-4 py-4 text-xs text-zinc-500">No loads logged yet.</div>
                    ) : (
                      <div className="divide-y divide-zinc-100">
                        {s.loads.map((l, i) => (
                          <div key={l.id} className="px-4 py-2.5 flex items-start gap-3 text-sm" data-testid={`load-row-${l.id}`}>
                            <span className="font-mono text-[10px] text-zinc-400 mt-0.5 w-8 shrink-0">{l.time || `#${i + 1}`}</span>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold">
                                {l.material}
                                {(l.tonnes || l.cubic_m) && (
                                  <span className="font-mono font-normal text-[#FF5F15] ml-2">
                                    {l.tonnes ? `${l.tonnes}t` : ""}{l.tonnes && l.cubic_m ? " / " : ""}{l.cubic_m ? `${l.cubic_m}m³` : ""}
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-zinc-500 flex items-center gap-1 flex-wrap mt-0.5">
                                {l.from_location || "—"} <ArrowRight className="w-3 h-3" /> {l.to_location || "—"}
                                {l.docket_number && <span className="font-mono ml-2">Docket {l.docket_number}</span>}
                                {l.job_id && <span className="text-[#FF5F15] font-bold ml-2">{l.job_number || ""} {l.job_title || ""}</span>}
                              </div>
                              {l.dispatch_id && <span className="text-[10px] font-bold text-zinc-500">From run</span>}
                              {l.docket_path && <button type="button" onClick={() => setDocketPhoto(l.docket_path)} className="block my-2" aria-label="View docket photo"><AuthImage path={l.docket_path} alt="Docket" className="w-14 h-14 object-cover border" /></button>}
                              {l.notes && <div className="text-[11px] text-zinc-500 italic mt-0.5">{l.notes}</div>}
                            </div>
                            {canModify && (
                              <button onClick={() => deleteLoad(s.id, l.id)} aria-label="Delete load" className="p-3 min-h-12 text-muted-foreground hover:text-red-600 shrink-0"
                                      data-testid={`load-delete-${l.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="px-4 py-3 border-t border-zinc-200 bg-muted/40 flex items-center gap-2 flex-wrap">
                      {canModify && (
                        <Button size="sm" onClick={() => setLoadDialog({ sheetId: s.id })}
                                className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase tracking-wider font-bold text-[10px]"
                                data-testid={`add-load-btn-${s.id}`}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add load
                        </Button>
                      )}
                      {isMineDraft && (s.loads || []).length > 0 && (
                        <Button size="sm" onClick={() => submitSheet(s)}
                                className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-[10px]"
                                data-testid={`submit-sheet-btn-${s.id}`}>
                          <Send className="w-3.5 h-3.5 mr-1" /> Submit day
                        </Button>
                      )}
                      {(isOffice || (isMineDraft && (s.loads || []).length === 0)) && (isOffice ? <>
                        <Button size="sm" variant="ghost" onClick={() => deleteSheet(s)}
                                className="rounded-sm text-zinc-500 hover:text-red-600 uppercase tracking-wider font-bold text-[10px] ml-auto"
                                data-testid={`delete-sheet-btn-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete sheet
                        </Button></> : <details className="ml-auto"><summary className="min-h-12 px-3 cursor-pointer flex items-center text-sm">More</summary><Button variant="ghost" className="min-h-12 text-red-600 dark:text-red-400" onClick={() => deleteSheet(s)}>Delete day’s record</Button></details>)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {docketPhoto && <PhotoLightbox paths={[docketPhoto]} startIndex={0} open onClose={() => setDocketPhoto(null)} />}
      {loadDialog && (
        <LoadDialog
          jobs={jobs}
          onClose={() => setLoadDialog(null)}
          onSave={async (payload) => {
            try {
              await api.post(`/truck-sheets/${loadDialog.sheetId}/loads`, payload);
              toast.success("Load added");
              setLoadDialog(null);
              load();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Failed to add load");
            }
          }}
        />
      )}
    </div>
  );
}
